import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, FileText, Download } from "lucide-react";
import { toast } from "sonner";

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [open, setOpen] = useState(false);
  const emptyForm = { title: "", type: "contract", content: "" };
  const [form, setForm] = useState(emptyForm);

  async function load() { const { data } = await api.get("/documents"); setDocs(data); }
  useEffect(() => { load(); }, []);

  async function save() {
    try { await api.post("/documents", form); toast.success("Dokument erstellt"); setOpen(false); setForm(emptyForm); load(); }
    catch { toast.error("Fehler"); }
  }

  function download(doc) {
    const blob = new Blob([`${doc.title}\n\nTyp: ${doc.type}\nErstellt: ${new Date(doc.created_at).toLocaleString('de-DE')}\n\n${doc.content}`], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${doc.title}.txt`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-testid="documents-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs text-muted-foreground font-bold uppercase tracking-[0.2em]">Verträge</div>
          <h1 className="font-heading text-4xl font-bold tracking-tight mt-2">Dokumente</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full h-11 px-6 font-semibold" data-testid="btn-new-doc"><Plus className="w-4 h-4 mr-1" /> Neues Dokument</Button>
          </DialogTrigger>
          <DialogContent className="rounded-3xl max-w-xl">
            <DialogHeader><DialogTitle>Neues Dokument</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Titel</Label><Input value={form.title} onChange={e=>setForm({...form, title:e.target.value})} data-testid="doc-title" className="rounded-full h-10 px-4" /></div>
              <div><Label>Typ</Label>
                <Select value={form.type} onValueChange={v=>setForm({...form, type:v})}>
                  <SelectTrigger className="rounded-full h-10 px-4" data-testid="doc-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="contract">Vertrag</SelectItem>
                    <SelectItem value="offer">Angebot</SelectItem>
                    <SelectItem value="other">Sonstiges</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Inhalt</Label><Textarea rows={8} value={form.content} onChange={e=>setForm({...form, content:e.target.value})} data-testid="doc-content" className="rounded-2xl" /></div>
            </div>
            <DialogFooter><Button onClick={save} className="rounded-full" data-testid="doc-save">Speichern</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {docs.map(d => (
          <Card key={d.id} className="rounded-3xl p-6 border-border/60 glass" data-testid={`doc-${d.id}`}>
            <div className="flex items-start gap-3">
              <div className="w-11 h-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center"><FileText className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold truncate">{d.title}</div>
                <Badge className="rounded-full mt-1 capitalize" variant="secondary">{d.type}</Badge>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-3 line-clamp-3">{d.content}</p>
            <Button size="sm" variant="outline" className="rounded-full mt-4 w-full" onClick={()=>download(d)} data-testid={`doc-download-${d.id}`}>
              <Download className="w-3 h-3 mr-1" /> Herunterladen
            </Button>
          </Card>
        ))}
        {docs.length === 0 && <div className="col-span-full text-center text-muted-foreground py-12">Noch keine Dokumente.</div>}
      </div>
    </div>
  );
}
